import type {CreativeAssetState} from './creativeAssetRenderer'

/** Ready-made test images so the creative assets tool can be tried without uploading anything. */

/** The purple editor background. */
const SAMPLE_BACKGROUND_URL = '/backgrounds/default.png'
const LOGO_URL = '/logo.svg'
const SAMPLE_COLORS = ['#7c5cff', '#ec4899', '#14b8a6', '#f59e0b', '#3b82f6'] as const
const SCREENSHOT_SIZE = {width: 585, height: 1266}
const ICON_SIZE = 512
const KEY_ART_SIZE = {width: 1200, height: 600}
const LOGO_ASPECT = 464 / 879
const SCREENSHOT_LOGO_WIDTH_RATIO = 0.42
const ICON_LOGO_HEIGHT_RATIO = 0.6
const KEY_ART_LOGO_HEIGHT_RATIO = 0.8

const SAMPLE_HEADLINE = 'Get more downloads'
const SAMPLE_SUBLINE = 'Make your app stand out'

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Could not load ${src}`))
    img.src = src
  })

let logoSvgPromise: Promise<string> | null = null
const fetchLogoSvg = (): Promise<string> => {
  logoSvgPromise ??= fetch(LOGO_URL).then((res) => res.text())
  return logoSvgPromise
}

/** The Ecranify logo recoloured (the source SVG is black fill + stroke). */
const loadLogo = async (color: string): Promise<HTMLImageElement> => {
  const svg = (await fetchLogoSvg()).replaceAll('"black"', `"${color}"`)
  return loadImage(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`)
}

const canvasToImage = (canvas: HTMLCanvasElement): Promise<HTMLImageElement> => loadImage(canvas.toDataURL('image/png'))

const drawCentered = (
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  cx: number,
  cy: number,
  height: number,
): void => {
  const width = height * LOGO_ASPECT
  ctx.drawImage(img, cx - width / 2, cy - height / 2, width, height)
}

/** Fake phone screenshot: coloured tile with a white logo. */
const buildScreenshot = async (color: string): Promise<HTMLImageElement> => {
  const {width, height} = SCREENSHOT_SIZE
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('2d context unavailable')
  const grad = ctx.createLinearGradient(0, 0, width, height)
  grad.addColorStop(0, color)
  grad.addColorStop(1, '#1a1230')
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, width, height)
  drawCentered(
    ctx,
    await loadLogo('#ffffff'),
    width / 2,
    height / 2,
    (width * SCREENSHOT_LOGO_WIDTH_RATIO) / LOGO_ASPECT,
  )
  return canvasToImage(canvas)
}

const buildIcon = async (color: string): Promise<HTMLImageElement> => {
  const canvas = document.createElement('canvas')
  canvas.width = ICON_SIZE
  canvas.height = ICON_SIZE
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('2d context unavailable')
  ctx.fillStyle = color
  ctx.fillRect(0, 0, ICON_SIZE, ICON_SIZE)
  drawCentered(ctx, await loadLogo('#ffffff'), ICON_SIZE / 2, ICON_SIZE / 2, ICON_SIZE * ICON_LOGO_HEIGHT_RATIO)
  return canvasToImage(canvas)
}

/** Transparent canvas with a big coloured logo — the "key art" sample. */
const buildKeyArt = async (color: string): Promise<HTMLImageElement> => {
  const {width, height} = KEY_ART_SIZE
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('2d context unavailable')
  drawCentered(ctx, await loadLogo(color), width / 2, height / 2, height * KEY_ART_LOGO_HEIGHT_RATIO)
  return canvasToImage(canvas)
}

export interface SampleAssets {
  bgImage: HTMLImageElement
  screenshots: HTMLImageElement[]
  icon: HTMLImageElement
  keyArt: HTMLImageElement
}

/** The purple editor background, three differently coloured screenshots, an icon and key art. */
export const loadSampleAssets = async (): Promise<SampleAssets> => {
  const [bgImage, screenshots, icon, keyArt] = await Promise.all([
    loadImage(SAMPLE_BACKGROUND_URL),
    Promise.all(SAMPLE_COLORS.slice(0, 3).map(buildScreenshot)),
    buildIcon(SAMPLE_COLORS[0]),
    buildKeyArt('#ffffff'),
  ])
  return {bgImage, screenshots, icon, keyArt}
}

/**
 * Patch that fills only what the user has not touched yet (still equal to `initial`), so samples that finish
 * loading after the user uploaded or typed something never overwrite it.
 */
export const sampleAssetsPatch = (
  prev: CreativeAssetState,
  initial: CreativeAssetState,
  samples: SampleAssets,
): Partial<CreativeAssetState> => ({
  ...(prev.screenshots.length === 0 && {screenshots: samples.screenshots}),
  ...(prev.icon === null && {icon: samples.icon}),
  ...(prev.keyArt === null && {keyArt: samples.keyArt}),
  ...(prev.bgImage === null && prev.bgMode === initial.bgMode && {bgMode: 'image' as const, bgImage: samples.bgImage}),
  ...(prev.headline === initial.headline && {headline: SAMPLE_HEADLINE}),
  ...(prev.subline === initial.subline && {subline: SAMPLE_SUBLINE}),
})
